import { watch } from '@thiememeulenhoff/lit-sdk';
import { translate } from '@thiememeulenhoff/translations';
import {
  getScoreLevel,
  NO_SCORE_TYPES,
  ScoreLevel,
  ScoreType,
} from '@thiememeulenhoff/types';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { SCORE_LEVEL_ICON_MAP } from './score-label.types';
import { tagName } from '../../../helpers';
import { LabelComponent } from '../label.component';
import { scoreLabelStyles } from './score-label.styles';

const tag = tagName('score-label');

@customElement(tag)
export class ScoreLabelComponent extends LabelComponent {
  static override styles = [...scoreLabelStyles];

  @property({ type: Number, reflect: true })
  score?: number;

  @property({ reflect: true })
  level?: ScoreLevel;

  @property({ type: Boolean, reflect: true })
  active = false;

  @property({ type: String, reflect: true, attribute: 'score-type' })
  scoreType?: ScoreType;

  @property({ type: String })
  label?: string;

  @property({ type: Boolean })
  neutral = false;

  @property({ type: Boolean, reflect: true })
  pill = false;

  @property({ type: Boolean, attribute: 'icon-score' })
  iconScore = false;

  @property({ type: Boolean, reflect: true })
  empty = false;

  myAriaLabel: string | null = null;

  @property({ type: String, reflect: true, attribute: 'aria-label' })
  override get ariaLabel(): string | null {
    if (this.myAriaLabel === null && typeof this.score === 'number') {
      return this.showScore
        ? `${Math.round(this.score)}%`
        : translate('content.score-label.completed');
    }

    return this.myAriaLabel;
  }

  override set ariaLabel(ariaLabel: string) {
    this.myAriaLabel = ariaLabel;
  }

  get showScore() {
    return this.scoreType ? !NO_SCORE_TYPES.includes(this.scoreType) : true;
  }

  @watch('score')
  onScoreChange() {
    this.level =
      typeof this.score === 'number'
        ? getScoreLevel(Math.round(this.score))
        : undefined;
  }

  protected override render() {
    if (this.empty) return html``;

    if (this.icon) return this.iconTemplate();

    if (this.label) return html`${this.label}`;

    if (this.scoreType === ScoreType.Disabled) {
      return html``;
    }

    if (typeof this.score === 'number') {
      if (this.iconScore) {
        return html`<a-icon
          class="score-icon"
          name="${this.scoreIcon()}"
          data-test="score-icon"
        ></a-icon>`;
      }
      if (!this.showScore)
        return html`<a-icon name="check" data-test="score-icon"></a-icon>`;
      return html`${Math.round(this.score)}%`;
    }

    if (this.iconScore) {
      return html`<a-icon
        class="score-icon"
        name="dash"
        data-test="score-icon"
      ></a-icon>`;
    }

    return html``;
  }

  scoreIcon() {
    const level = getScoreLevel(this.score ?? 0) ?? ScoreLevel.Excellent;

    return SCORE_LEVEL_ICON_MAP[level];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-score-label': ScoreLabelComponent;
  }
}
